<script>
  import { authState, register, login, magicLink, googleLogin, logout, getKnownUsers } from '../stores/auth.js';
  import { licenseState } from '../stores/license.js';
  import { appStatus, pushNotice } from '../stores/ui.js';
  import { onMount } from 'svelte';
  import { t, locale as localeStore } from 'svelte-i18n';
  import { LANGUAGE_OPTIONS } from '../i18n/languages.js';
  import { setAppLocale } from '../i18n/index.js';
  import { saveLanguageSetting } from '../i18n/languageStore.js';

  let email = '';
  let password = '';
  let knownUsers = [];
  let selectedLocale = 'de';

  onMount(() => {
    appStatus.set({
      primary: $t('auth.account'),
      secondary: 'Auth'
    });
    knownUsers = getKnownUsers();
    selectedLocale = $localeStore;
  });

  async function handleLocaleChange(event) {
    const next = event?.target?.value;
    const normalized = setAppLocale(next);
    selectedLocale = normalized;
    // Persist to per-user settings (guest before login; user after login)
    await saveLanguageSetting(normalized);
  }

  async function handleRegister() {
    try {
      await register(email, password);
      pushNotice($t('auth.registeredAndLoggedIn'), 'success');
    } catch (error) {
      pushNotice(error.message || $t('auth.registerFailed'), 'error');
    }
  }

  async function handleLogin() {
    try {
      await login(email, password);
      pushNotice($t('auth.loggedIn'), 'success');
      // stay on current view; admin can navigate manually
    } catch (error) {
      pushNotice(error.message || $t('auth.loginFailed'), 'error');
    }
  }

  async function handleMagic() {
    try {
      await magicLink(email);
      pushNotice($t('auth.magicSimulated'), 'success');
    } catch (error) {
      pushNotice(error.message || $t('auth.magicFailed'), 'error');
    }
  }

  async function handleGoogle() {
    try {
      await googleLogin(email);
      pushNotice($t('auth.googleSimulated'), 'success');
    } catch (error) {
      pushNotice(error.message || $t('auth.googleFailed'), 'error');
    }
  }
</script>

<section class="panel">
  <div class="panel-title">{$t('auth.account')}</div>

  <div class="lang">
    <div class="lang-title">{$t('common.language')}</div>
    <select bind:value={selectedLocale} on:change={handleLocaleChange}>
      {#each LANGUAGE_OPTIONS as opt}
        <option value={opt.code}>{opt.label}</option>
      {/each}
    </select>
    <div class="hint">{$t('auth.languageHint')}</div>
  </div>

  {#if $authState.isAuthenticated}
    <div class="status">{$t('auth.signedInAs')} <strong>{$authState.email}</strong></div>
    <div class="license">
      {$t('auth.plan')}: <strong>{$licenseState.plan}</strong> · {$t('auth.trialEnds')}: <strong>{$licenseState.trialEndsAt || '—'}</strong>
    </div>
    <button class="secondary" on:click={logout}>{$t('auth.logout')}</button>
  {:else}
    <div class="form">
      {#if knownUsers.length > 0}
        <div class="saved">
          <div class="saved-title">{$t('auth.savedAccounts')}</div>
          <div class="saved-list">
            {#each knownUsers as user}
              <button class="saved-item" on:click={() => (email = user.email)}>{user.email}</button>
            {/each}
          </div>
        </div>
      {/if}
      <input placeholder={$t('auth.email')} bind:value={email} />
      <input type="password" placeholder={$t('auth.password')} bind:value={password} />
      <div class="actions">
        <button class="primary" on:click={handleLogin}>{$t('auth.login')}</button>
        <button class="secondary" on:click={handleRegister}>{$t('auth.register')}</button>
      </div>
      <div class="actions">
        <button class="ghost" on:click={handleMagic}>{$t('auth.magicLink')}</button>
        <button class="ghost" on:click={handleGoogle}>{$t('auth.googleLogin')}</button>
      </div>
    </div>
  {/if}
</section>

<section class="panel">
  <div class="panel-title">Manual Paywall</div>
  <p>Licenses are granted manually for now. Use the admin endpoint to activate a plan.</p>
  <code>POST /admin/grant</code>
</section>

<style>
  .panel {
    background: #fff;
    border-radius: 18px;
    padding: 1.5rem;
    border: 1px solid rgba(31, 41, 51, 0.08);
    display: grid;
    gap: 1rem;
  }

  .panel-title {
    font-weight: 700;
  }

  .status,
  .license {
    color: var(--muted);
  }

  .form {
    display: grid;
    gap: 0.75rem;
  }

  .saved {
    background: rgba(255, 250, 241, 0.6);
    border-radius: 12px;
    padding: 0.75rem;
    border: 1px solid rgba(31, 41, 51, 0.1);
  }

  .saved-title {
    font-weight: 600;
    margin-bottom: 0.5rem;
  }

  .saved-list {
    display: grid;
    gap: 0.5rem;
  }

  .saved-item {
    text-align: left;
    background: rgba(31, 41, 51, 0.08);
    border: none;
    padding: 0.4rem 0.6rem;
    border-radius: 999px;
    cursor: pointer;
  }

  .lang {
    display: grid;
    gap: 0.5rem;
    padding: 0.75rem;
    border-radius: 12px;
    border: 1px solid rgba(31, 41, 51, 0.1);
    background: rgba(255, 250, 241, 0.6);
  }

  .lang-title {
    font-weight: 700;
  }

  .hint {
    color: var(--muted);
    font-size: 0.85rem;
  }

  input {
    padding: 0.6rem 0.8rem;
    border-radius: 12px;
    border: 1px solid rgba(31, 41, 51, 0.15);
    font-size: 0.95rem;
  }

  .actions {
    display: flex;
    gap: 0.75rem;
    flex-wrap: wrap;
  }

  .primary,
  .secondary,
  .ghost {
    padding: 0.6rem 1.2rem;
    border-radius: 999px;
    border: none;
    cursor: pointer;
    font-weight: 600;
  }

  .primary {
    background: var(--ink);
    color: #fff5e1;
  }

  .secondary {
    background: rgba(31, 41, 51, 0.1);
  }

  .ghost {
    background: rgba(31, 41, 51, 0.08);
  }
</style>
